import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, doc, updateDoc, deleteDoc, where, orderBy } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { User } from '../../types/user';
import { Organization } from '../../types/organization';
import toast from 'react-hot-toast';
import { 
  UserIcon, 
  BuildingOfficeIcon, 
  PencilIcon, 
  TrashIcon,
  EyeIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

interface SystemUser {
  id: string;
  email: string;
  displayName: string;
  photoURL: string;
  role: 'super_admin' | 'admin' | 'user' | 'customer';
  organizationId?: string;
  organizationName?: string;
  isActive?: boolean;
  createdAt?: any; // Firebase timestamp
  lastLogin?: any; // Firebase timestamp
  permissions?: any;
  customerId?: string;
}

const SystemUserManager: React.FC = () => {
  const [users, setUsers] = useState<SystemUser[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [organizationFilter, setOrganizationFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedUser, setSelectedUser] = useState<SystemUser | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch organizations
      const orgsQuery = query(collection(db, 'organizations'));
      const orgsSnapshot = await getDocs(orgsQuery);
      const orgsData = orgsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Organization[];
      setOrganizations(orgsData);

      // Fetch users
      const usersQuery = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
      const usersSnapshot = await getDocs(usersQuery);
      const usersData = usersSnapshot.docs.map(doc => {
        const userData = { id: doc.id, ...doc.data() } as SystemUser;
        
        // Add organization name if user has organizationId
        if (userData.organizationId) {
          const org = orgsData.find(o => o.id === userData.organizationId);
          userData.organizationName = org?.name || 'Ismeretlen szervezet';
        }
        
        return userData;
      });
      
      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Hiba történt az adatok betöltése során');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId: string, newRole: string) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, { role: newRole });
      
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId ? { ...user, role: newRole as 'super_admin' | 'admin' | 'user' | 'customer' } : user
        )
      );
      
      toast.success('Felhasználó jogosultság frissítve');
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error('Hiba történt a jogosultság frissítése során');
    }
  };

  const handleUserStatusChange = async (userId: string, isActive: boolean) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, { isActive });
      
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId ? { ...user, isActive } : user
        )
      );
      
      toast.success(`Felhasználó ${isActive ? 'aktiválva' : 'letiltva'}`);
    } catch (error) {
      console.error('Error updating user status:', error);
      toast.error('Hiba történt a felhasználó státuszának frissítése során');
    }
  };

  const handleOrganizationChange = async (userId: string, organizationId: string) => {
    try {
      const userRef = doc(db, 'users', userId);
      const organization = organizations.find(org => org.id === organizationId);
      
      await updateDoc(userRef, { 
        organizationId: organizationId || undefined,
        updatedAt: new Date()
      });
      
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId ? { 
            ...user, 
            organizationId: organizationId || undefined,
            organizationName: organization?.name || undefined
          } : user
        )
      );
      
      toast.success(`Felhasználó szervezethez rendelve: ${organization?.name || 'Nincs szervezet'}`);
    } catch (error) {
      console.error('Error updating user organization:', error);
      toast.error('Hiba történt a szervezet hozzárendelése során');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Biztosan törölni szeretné ezt a felhasználót?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'users', userId));
      setUsers(prevUsers => prevUsers.filter(user => user.id !== userId));
      toast.success('Felhasználó törölve');
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Hiba történt a felhasználó törlése során');
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'super_admin':
        return <ShieldCheckIcon className="h-4 w-4 text-red-500" />;
      case 'admin':
        return <ShieldExclamationIcon className="h-4 w-4 text-orange-500" />;
      default:
        return <UserIcon className="h-4 w-4 text-gray-500" />;
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'bg-red-100 text-red-800';
      case 'admin':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.organizationName?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesOrg = organizationFilter === 'all' || user.organizationId === organizationFilter;
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && user.isActive !== false) ||
                         (statusFilter === 'inactive' && user.isActive === false);
    
    return matchesSearch && matchesRole && matchesOrg && matchesStatus;
  });

  const userStats = {
    total: users.length,
    active: users.filter(u => u.isActive !== false).length,
    inactive: users.filter(u => u.isActive === false).length,
    superAdmins: users.filter(u => u.role === 'super_admin').length,
    admins: users.filter(u => u.role === 'admin').length,
    users: users.filter(u => u.role === 'user').length
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Rendszerfelhasználók áttekintése</h2>
        
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{userStats.total}</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Összes</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">{userStats.active}</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Aktív</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">{userStats.inactive}</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Letiltott</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">{userStats.superAdmins}</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Super Admin</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{userStats.admins}</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Admin</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">{userStats.users}</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Felhasználó</div>
          </div>
        </div>
      </div>

      {/* Modern Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              placeholder="Keresés felhasználók között..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 w-full h-12 rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 dark:focus:border-blue-400 focus:ring-blue-500 dark:focus:ring-blue-400 text-sm placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
          
                     {/* Filter Chips */}
           <div className="flex flex-wrap gap-3 animate-fade-in">
            {/* Role Filter */}
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Jogosultság:</span>
              <div className="flex space-x-1">
                {[
                  { value: 'all', label: 'Összes', count: userStats.total },
                  { value: 'super_admin', label: 'Super Admin', count: userStats.superAdmins },
                  { value: 'admin', label: 'Admin', count: userStats.admins },
                  { value: 'user', label: 'Felhasználó', count: userStats.users }
                ].map((role) => (
                  <button
                    key={role.value}
                    onClick={() => setRoleFilter(role.value)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      roleFilter === role.value
                        ? 'bg-blue-100 text-blue-700 border border-blue-200 dark:bg-blue-900 dark:text-blue-300 dark:border-blue-700'
                        : 'bg-gray-100 text-gray-600 border border-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {role.label} ({role.count})
                  </button>
                ))}
              </div>
            </div>
            
                         {/* Status Filter */}
             <div className="flex items-center space-x-2">
               <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Státusz:</span>
               <div className="flex space-x-1">
                 {[
                   { value: 'all', label: 'Összes', count: userStats.total },
                   { value: 'active', label: 'Aktív', count: userStats.active },
                   { value: 'inactive', label: 'Letiltott', count: userStats.inactive }
                 ].map((status) => (
                   <button
                     key={status.value}
                     onClick={() => setStatusFilter(status.value)}
                     className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                       statusFilter === status.value
                         ? 'bg-emerald-100 text-emerald-700 border border-emerald-200 dark:bg-emerald-900 dark:text-emerald-300 dark:border-emerald-700'
                         : 'bg-gray-100 text-gray-600 border border-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600'
                     }`}
                   >
                     {status.label} ({status.count})
                   </button>
                 ))}
               </div>
             </div>
             
             {/* Organization Filter */}
             <div className="flex items-center space-x-2">
               <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Szervezet:</span>
               <div className="flex space-x-1 flex-wrap">
                 <button
                   onClick={() => setOrganizationFilter('all')}
                   className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                     organizationFilter === 'all'
                       ? 'bg-purple-100 text-purple-700 border border-purple-200 dark:bg-purple-900 dark:text-purple-300 dark:border-purple-700'
                       : 'bg-gray-100 text-gray-600 border border-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600'
                   }`}
                 >
                   Összes szervezet
                 </button>
                 {organizations.slice(0, 3).map(org => (
                   <button
                     key={org.id}
                     onClick={() => setOrganizationFilter(org.id)}
                     className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                       organizationFilter === org.id
                         ? 'bg-purple-100 text-purple-700 border border-purple-200 dark:bg-purple-900 dark:text-purple-300 dark:border-purple-700'
                         : 'bg-gray-100 text-gray-600 border border-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600'
                     }`}
                   >
                     {org.name}
                   </button>
                 ))}
                 {organizations.length > 3 && (
                   <div className="relative">
                     <button
                       className="px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600"
                       onClick={() => {
                         // Simple toggle for showing more organizations
                         const remainingOrgs = organizations.slice(3);
                         if (remainingOrgs.length > 0) {
                           alert(`További szervezetek: ${remainingOrgs.map(org => org.name).join(', ')}`);
                         }
                       }}
                     >
                       +{organizations.length - 3} további
                     </button>
                   </div>
                 )}
               </div>
             </div>
          </div>
          
                     {/* Active Filters & Clear */}
           {(searchTerm || roleFilter !== 'all' || organizationFilter !== 'all' || statusFilter !== 'all') && (
             <div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
               <div className="flex items-center space-x-2">
                 <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Aktív szűrők:</span>
                 <div className="flex space-x-1 flex-wrap">
                   {searchTerm && (
                     <span className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-md border border-blue-200 dark:bg-blue-900 dark:text-blue-300">
                       🔍 "{searchTerm}"
                     </span>
                   )}
                   {roleFilter !== 'all' && (
                     <span className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-md border border-blue-200 dark:bg-blue-900 dark:text-blue-300">
                       👤 {roleFilter === 'super_admin' ? 'Super Admin' : roleFilter === 'admin' ? 'Admin' : 'Felhasználó'}
                     </span>
                   )}
                   {statusFilter !== 'all' && (
                     <span className="px-2 py-1 bg-emerald-50 text-emerald-700 text-xs rounded-md border border-emerald-200 dark:bg-emerald-900 dark:text-emerald-300">
                       🔄 {statusFilter === 'active' ? 'Aktív' : 'Letiltott'}
                     </span>
                   )}
                   {organizationFilter !== 'all' && (
                     <span className="px-2 py-1 bg-purple-50 text-purple-700 text-xs rounded-md border border-purple-200 dark:bg-purple-900 dark:text-purple-300">
                       🏢 {organizations.find(org => org.id === organizationFilter)?.name}
                     </span>
                   )}
                 </div>
               </div>
               <button
                 onClick={() => {
                   setSearchTerm('');
                   setRoleFilter('all');
                   setOrganizationFilter('all');
                   setStatusFilter('all');
                 }}
                 className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 border border-gray-200 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
               >
                 ✕ Szűrők törlése
               </button>
             </div>
           )}
          
          {/* Results Count */}
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {filteredUsers.length} felhasználó található {userStats.total} összesből
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Felhasználó
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Szervezet
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Jogosultság
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Státusz
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Létrehozva
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Műveletek
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                          <UserIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{user.displayName}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <BuildingOfficeIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 mr-2" />
                      <span className="text-sm text-gray-900 dark:text-gray-100">
                        {user.organizationName || 'Nincs szervezet'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {getRoleIcon(user.role)}
                      <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${getRoleBadgeColor(user.role)} dark:bg-gray-700 dark:text-gray-300`}>
                        {user.role === 'super_admin' ? 'Super Admin' : 
                         user.role === 'admin' ? 'Admin' : 
                         user.role === 'customer' ? 'Ügyfél' : 'Felhasználó'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      user.isActive !== false ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                    }`}>
                      {user.isActive !== false ? 'Aktív' : 'Letiltott'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {user.createdAt ? new Date(user.createdAt.seconds * 1000).toLocaleDateString('hu-HU') : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setSelectedUser(user)}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                        title="Részletek megtekintése"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleUserStatusChange(user.id, user.isActive === false)}
                        className={`${user.isActive !== false ? 'text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300' : 'text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300'}`}
                        title={user.isActive !== false ? 'Letiltás' : 'Aktiválás'}
                      >
                        {user.isActive !== false ? (
                          <ExclamationTriangleIcon className="h-4 w-4" />
                        ) : (
                          <ShieldCheckIcon className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.id)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                        title="Törlés"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <UserIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">Nincs találat</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Nem található felhasználó a megadott szűrők alapján.</p>
          </div>
        )}
      </div>

      {/* Modern User Details Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="h-12 w-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    <UserIcon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white">
                      {selectedUser.displayName}
                    </h3>
                    <p className="text-blue-100 text-sm">
                      {selectedUser.email}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedUser(null)}
                  className="p-2 hover:bg-white/10 rounded-full transition-colors"
                >
                  <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* User Info Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Basic Info Card */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <UserIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">Alapadatok</h4>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Teljes név</label>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{selectedUser.displayName}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Email cím</label>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{selectedUser.email}</p>
                    </div>
                  </div>
                </div>

                {/* Organization Card */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <BuildingOfficeIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">Szervezet</h4>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Szervezet neve</label>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {selectedUser.organizationName || 'Nincs szervezet'}
                    </p>
                  </div>
                </div>

                {/* Activity Card */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <svg className="h-5 w-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">Aktivitás</h4>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Regisztrált</label>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {selectedUser.createdAt ? new Date(selectedUser.createdAt.seconds * 1000).toLocaleDateString('hu-HU') : 'Ismeretlen'}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Utolsó bejelentkezés</label>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {selectedUser.lastLogin ? new Date(selectedUser.lastLogin.seconds * 1000).toLocaleString('hu-HU') : 'Soha'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Status Card */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className={`h-3 w-3 rounded-full ${selectedUser.isActive !== false ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">Státusz</h4>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Fiók állapota</label>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        selectedUser.isActive !== false ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                      }`}>
                        {selectedUser.isActive !== false ? 'Aktív' : 'Letiltott'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Controls Section */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Beállítások</h4>
                
                {/* Role Selection */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Jogosultság szint</label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { value: 'user', label: 'Felhasználó', icon: UserIcon, color: 'gray' },
                        { value: 'customer', label: 'Ügyfél', icon: UserIcon, color: 'blue' },
                        { value: 'admin', label: 'Admin', icon: ShieldExclamationIcon, color: 'orange' },
                        { value: 'super_admin', label: 'Super Admin', icon: ShieldCheckIcon, color: 'red' }
                      ].map((role) => {
                        const Icon = role.icon;
                        const isSelected = selectedUser.role === role.value;
                        return (
                          <button
                            key={role.value}
                            onClick={() => handleRoleChange(selectedUser.id, role.value)}
                            className={`flex items-center space-x-2 px-4 py-3 rounded-xl border-2 transition-all ${
                              isSelected
                                ? role.color === 'gray' 
                                  ? 'border-gray-300 bg-gray-50 text-gray-900 dark:bg-gray-700 dark:text-gray-100'
                                  : role.color === 'blue'
                                  ? 'border-blue-300 bg-blue-50 text-blue-900 dark:bg-blue-900 dark:text-blue-300'
                                  : role.color === 'orange'
                                  ? 'border-orange-300 bg-orange-50 text-orange-900 dark:bg-orange-900 dark:text-orange-300'
                                  : 'border-red-300 bg-red-50 text-red-900 dark:bg-red-900 dark:text-red-300'
                                : 'border-gray-200 bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600'
                            }`}
                          >
                            <Icon className="h-5 w-5" />
                            <span className="font-medium">{role.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Status Toggle */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Fiók státusz</label>
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => handleUserStatusChange(selectedUser.id, true)}
                        className={`flex items-center space-x-2 px-4 py-3 rounded-xl border-2 transition-all ${
                          selectedUser.isActive !== false
                            ? 'border-green-300 bg-green-50 text-green-900 dark:bg-green-900 dark:text-green-300'
                            : 'border-gray-200 bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                      >
                        <div className="h-3 w-3 rounded-full bg-green-500"></div>
                        <span className="font-medium">Aktív</span>
                      </button>
                      <button
                        onClick={() => handleUserStatusChange(selectedUser.id, false)}
                        className={`flex items-center space-x-2 px-4 py-3 rounded-xl border-2 transition-all ${
                          selectedUser.isActive === false
                            ? 'border-red-300 bg-red-50 text-red-900 dark:bg-red-900 dark:text-red-300'
                            : 'border-gray-200 bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                      >
                        <div className="h-3 w-3 rounded-full bg-red-500"></div>
                        <span className="font-medium">Letiltott</span>
                      </button>
                    </div>
                  </div>

                  {/* Organization Assignment */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Szervezet hozzárendelés</label>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <BuildingOfficeIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          Jelenlegi szervezet: <strong>{selectedUser.organizationName || 'Nincs szervezet'}</strong>
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => handleOrganizationChange(selectedUser.id, '')}
                          className={`flex items-center space-x-2 px-3 py-2 rounded-lg border-2 transition-all ${
                            !selectedUser.organizationId
                              ? 'border-gray-300 bg-gray-50 text-gray-900 dark:bg-gray-700 dark:text-gray-100'
                              : 'border-gray-200 bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600'
                          }`}
                        >
                          <span className="text-sm font-medium">Nincs szervezet</span>
                        </button>
                        {organizations.map(org => (
                          <button
                            key={org.id}
                            onClick={() => handleOrganizationChange(selectedUser.id, org.id)}
                            className={`flex items-center space-x-2 px-3 py-2 rounded-lg border-2 transition-all ${
                              selectedUser.organizationId === org.id
                                ? 'border-blue-300 bg-blue-50 text-blue-900 dark:bg-blue-900 dark:text-blue-300'
                                : 'border-gray-200 bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600'
                            }`}
                          >
                            <BuildingOfficeIcon className="h-4 w-4" />
                            <span className="text-sm font-medium">{org.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Danger Zone */}
              <div className="border-t border-red-200 dark:border-red-800 pt-6">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
                    <h4 className="font-medium text-red-900 dark:text-red-100">Veszélyes műveletek</h4>
                  </div>
                  <p className="text-sm text-red-700 dark:text-red-300 mb-3">
                    Ezek a műveletek visszavonhatatlanok. Kérjük, legyen óvatos!
                  </p>
                  <button
                    onClick={() => {
                      if (window.confirm('Biztosan törölni szeretné ezt a felhasználót? Ez a művelet visszavonhatatlan!')) {
                        handleDeleteUser(selectedUser.id);
                        setSelectedUser(null);
                      }
                    }}
                    className="flex items-center space-x-2 px-4 py-2 bg-red-600 dark:bg-red-500 text-white rounded-lg hover:bg-red-700 dark:hover:bg-red-600 transition-colors"
                  >
                    <TrashIcon className="h-4 w-4" />
                    <span>Felhasználó törlése</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemUserManager; 